<script setup lang="ts">
/**
 * One tool call paired with its result.
 *
 * The reducer emits a `tool_call` step and, later, a `tool_result` step
 * correlated by `toolCallId`; this pairs them so a call renders the moment it is
 * requested and fills in when it resolves, rather than appearing only once
 * complete.
 */
import { computed, ref } from 'vue';

import type { ReasoningStep } from '../types/message';

const props = defineProps<{
  call: ReasoningStep;
  result?: ReasoningStep;
}>();

const open = ref(false);

const pending = computed(() => props.result === undefined);

/** Pretty-print JSON when it is JSON; show it raw when it is not. */
function pretty(text: string | undefined): string {
  if (!text) return '';
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

const args = computed(() => pretty(props.call.argumentsJson));
const output = computed(() => pretty(props.result?.output ?? props.result?.content));

const copied = ref('');

/** Copy a pane's text. Silent on denial rather than claiming a copy that failed. */
async function copy(what: 'args' | 'output', text: string) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    copied.value = what;
    setTimeout(() => {
      if (copied.value === what) copied.value = '';
    }, 1200);
  } catch {
    /* clipboard unavailable (insecure context / denied) */
  }
}
</script>

<template>
  <div
    data-testid="tool-call-card"
    class="rounded-lg border border-gray-200 dark:border-gray-700"
    :data-pending="pending ? 'true' : 'false'"
  >
    <button
      type="button"
      data-testid="tool-call-toggle"
      class="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
      :aria-expanded="open"
      @click="open = !open"
    >
      <span
        class="text-gray-400 transition-transform"
        :class="open ? 'rotate-90' : ''"
        aria-hidden="true"
        >›</span
      >
      <span
        class="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
      >
        <svg class="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M14.7 6.3a4 4 0 0 1-5 5L4 17v3h3l5.7-5.7a4 4 0 0 1 5-5l-2.5 2.5 1.8 1.8L19.5 11a4 4 0 0 0-4.8-4.7z" />
        </svg>
        Tool
      </span>
      <span class="truncate font-mono text-xs font-medium text-gray-700 dark:text-gray-300">{{
        call.toolName
      }}</span>
      <span
        v-if="pending"
        data-testid="tool-call-pending"
        class="ml-auto flex shrink-0 items-center gap-1.5 text-[10px] text-gray-400"
      >
        <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" aria-hidden="true" />
        running…
      </span>
      <span
        v-else
        data-testid="tool-call-done"
        class="ml-auto flex shrink-0 items-center gap-1 text-[10px] text-gray-400"
      >
        <svg class="h-3 w-3 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        done
      </span>
    </button>

    <div v-if="open" class="space-y-3 border-t border-gray-100 px-3 pb-3 pt-2 dark:border-gray-800">
      <div v-if="args">
        <div class="mb-1 flex items-center justify-between">
          <p class="text-[10px] font-medium uppercase tracking-wide text-gray-400">Arguments</p>
          <button
            type="button"
            class="rounded px-1.5 py-0.5 text-[10px] text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            @click="copy('args', args)"
          >
            {{ copied === 'args' ? 'Copied' : 'Copy' }}
          </button>
        </div>
        <pre
          class="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-gray-50 p-2 font-mono text-xs leading-relaxed text-gray-700 dark:bg-black/30 dark:text-gray-300"
          >{{ args }}</pre
        >
      </div>
      <div v-if="output">
        <div class="mb-1 flex items-center justify-between">
          <p class="text-[10px] font-medium uppercase tracking-wide text-gray-400">Result</p>
          <button
            type="button"
            class="rounded px-1.5 py-0.5 text-[10px] text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            @click="copy('output', output)"
          >
            {{ copied === 'output' ? 'Copied' : 'Copy' }}
          </button>
        </div>
        <!-- Interpolated, never v-html: a tool result can carry attacker-controlled
             text — a scraped page, a command's stdout. -->
        <pre
          data-testid="tool-call-output"
          class="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-gray-50 p-2 font-mono text-xs leading-relaxed text-gray-700 dark:bg-black/30 dark:text-gray-300"
          >{{ output }}</pre
        >
      </div>
    </div>
  </div>
</template>
